import { CheckCircleOutlined, EditFilled, EyeFilled, WarningTwoTone } from "@ant-design/icons";
import { Button, Progress, Space, Tooltip } from "antd";
import GoldenNominalBadge from "components/atoms/GoldenNominalBadge";
import DataTable from "components/molecules/DataTable";
import ProhibitedArea from "components/molecules/ProhibitedArea";
import { isDatasetGolden } from "common/goldenNominal";
import { useAppSelector } from "hooks";
import moment from "moment";
import numeral from "numeral";
import { useState } from "react";
import CypressTestIds from "../../../cypress/CypressTestIds";

const DataSetRecentList = ({ permission, dataSetList, onViewDataSet, onEditDataSet, onDataSetSelected }) => {
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const goldenBySchema = useAppSelector((s) => s.goldenNominal?.goldenBySchema);
    const onClickRow = (record) => {
        return {
            onClick: () => {
                setSelectedRowKeys([`${record.dataSetId}-${record.updatedDate}`]);
                onDataSetSelected && onDataSetSelected(record.dataSetId);
            },
        };
    };

    return !permission ? (
        <ProhibitedArea></ProhibitedArea>
    ) : (
        <DataTable
            dataTestId={CypressTestIds.RECENT_LIST_DATASET_TABLE}
            hideSelectionColumn
            tableProps={{
                size: "small",
                sticky: true,
                rowKey: (record) => `${record["dataSetId"]}-${record["updatedDate"]}`,
                dataSource: dataSetList,
                onRow: onClickRow,
                rowClassName: (record) => (isDatasetGolden(goldenBySchema, record.schemaId, record.dataSetId, record.isGoldenNominal) ? "bg-amber-50" : ""),
                rowSelection: {
                    selectedRowKeys,
                    type: "radio",
                },
                pagination: false,
                columns: [
                    {
                        width: "4rem",
                        fixed: "left",
                        render: (value, dataSet, index) => (
                            <Space.Compact size="small" className="gap-1">
                                <Tooltip title="Edit Dataset">
                                    <Button
                                        type="text"
                                        icon={<EditFilled style={{ color: "#2185d0" }} />}
                                        disabled={!permission.canEdit}
                                        onClick={(event) => {
                                            event && event.stopPropagation && event.stopPropagation();
                                            onEditDataSet(dataSet);
                                        }}
                                        data-testid={CypressTestIds.RECENT_LIST_DATASET_EDIT_BUTTON}
                                    />
                                </Tooltip>
                                <Tooltip title="View Dataset">
                                    <Button
                                        type="text"
                                        icon={<EyeFilled style={{ color: "#2185d0" }} />}
                                        disabled={!permission.canView}
                                        onClick={(event) => {
                                            event && event.stopPropagation && event.stopPropagation();
                                            onViewDataSet(dataSet);
                                        }}
                                        data-testid={CypressTestIds.RECENT_LIST_DATASET_VIEW_BUTTON}
                                    />
                                </Tooltip>
                            </Space.Compact>
                        ),
                    },
                    {
                        width: "10rem",
                        dataIndex: "recordCount",
                        title: "Size",
                        ellipsis: {
                            showTitle: false,
                        },
                        render: (value, dataSet, index) => {
                            let numberOfDigits = Math.max(Math.ceil(Math.log10(value)), 2);
                            let percentage = (value / Math.pow(10, numberOfDigits)) * 100;
                            let formatValue = value ? numeral(value).format("0a") : 0;
                            return (
                                <>
                                    <Tooltip placement="topLeft" title={value}>
                                        <Progress style={{ width: "6rem" }} percent={percentage} size="small" format={(percent) => formatValue} />
                                    </Tooltip>
                                    {dataSet.erroredRecordsCount ? (
                                        <Tooltip placement="topLeft" className="mr-2" title={`Contains ${dataSet.erroredRecordsCount} error row(s)`}>
                                            <WarningTwoTone className="mx-2" twoToneColor="#FF0000" />
                                        </Tooltip>
                                    ) : (
                                        <CheckCircleOutlined className="mx-2" style={{ color: "#00cf18" }} />
                                    )}
                                </>
                            );
                        },
                    },
                    {
                        width: "15%",
                        dataIndex: "dataSetName",
                        title: "DataSet",
                        ellipsis: {
                            showTitle: false,
                        },
                        render: (value, dataSet, index) => (
                            <span className="inline-flex items-center gap-2">
                                <Tooltip placement="topLeft" title={value}>
                                    <span className="truncate">{value}</span>
                                </Tooltip>
                                {isDatasetGolden(goldenBySchema, dataSet.schemaId, dataSet.dataSetId, dataSet.isGoldenNominal) && <GoldenNominalBadge label="GOLDEN" />}
                            </span>
                        ),
                    },
                    {
                        width: "20%",
                        dataIndex: "dataSetDescription",
                        title: "Description",
                        ellipsis: {
                            showTitle: false,
                        },
                        render: (value, dataSet, index) => (
                            <Tooltip placement="topLeft" title={value}>
                                {value}
                            </Tooltip>
                        ),
                    },
                    {
                        width: "20%",
                        dataIndex: "schemaName",
                        title: "Parent Schema",
                        ellipsis: {
                            showTitle: false,
                        },
                        render: (value, dataSet, index) => (
                            <Tooltip placement="topLeft" title={value}>
                                {value}
                            </Tooltip>
                        ),
                    },
                    {
                        width: "15%",
                        dataIndex: "businessAreaName",
                        title: "Business Area",
                        ellipsis: {
                            showTitle: false,
                        },
                        render: (value, dataSet, index) => (
                            <Tooltip placement="topLeft" title={value}>
                                {value}
                            </Tooltip>
                        ),
                    },
                    {
                        width: "15%",
                        dataIndex: "updatedDate",
                        title: "Last Updated",
                        ellipsis: {
                            showTitle: false,
                        },
                        render: (value, dataSet, index) => {
                            let dateValue = moment.utc(value).toDate().toLocaleString();
                            return (
                                <Tooltip placement="topLeft" title={dateValue}>
                                    {dateValue}
                                </Tooltip>
                            );
                        },
                    },
                    {
                        width: "15%",
                        dataIndex: "updatedByEmail",
                        title: "Updated By",
                        ellipsis: {
                            showTitle: false,
                        },
                        render: (value, dataSet, index) => (
                            <Tooltip placement="topLeft" title={value}>
                                {value}
                            </Tooltip>
                        ),
                    },
                ],
            }}
        />
    );
};

export default DataSetRecentList;
